/**
 * Chat access gating.
 *
 * The first question on a profile is free; the second triggers a paywall.
 * Entitlement comes from the signed-in account, which the server decides and
 * the browser cannot edit. The local flag below is only a fallback for people
 * who paid on this device before accounts existed.
 */

const UNLOCK_KEY = 'astrobot.access.unlocked';

// Dodo Payments hosted checkout for the Astrobot product.
export const DODO_PRODUCT_ID = 'pdt_0NgER9AC7ND5biFmsYwL9';
const DODO_CHECKOUT_BASE = 'https://checkout.dodopayments.com/buy';

/** Questions answered for free before the paywall appears. */
export const FREE_QUESTIONS = 1;

/**
 * Server-verified entitlement, set by App from the signed-in account.
 * Falls back to the local flag only when signed out, so someone who paid on
 * this device before accounts existed is not locked out.
 */
let serverEntitled = null;

export function setServerEntitlement(value) {
  serverEntitled = value;
}

/**
 * The server refused a question for want of a subscription.
 *
 * Its verdict is authoritative, so clear the legacy local flag: leaving it set
 * would keep the client gate open against a server that is refusing, and the
 * two would disagree on every question.
 */
export function noteServerRefusal() {
  serverEntitled = false;
  setUnlocked(false);
}

export function isUnlocked() {
  // The account is authoritative when we have one: it is the only source a
  // person cannot edit, and it follows them between devices.
  if (serverEntitled !== null) return serverEntitled === true;
  try {
    return localStorage.getItem(UNLOCK_KEY) === 'true';
  } catch (e) {
    // Private browsing or blocked storage: fail open rather than locking the
    // person out of a product they may have paid for.
    return true;
  }
}

export function setUnlocked(value = true) {
  try {
    if (value) localStorage.setItem(UNLOCK_KEY, 'true');
    else localStorage.removeItem(UNLOCK_KEY);
  } catch (e) {
    // Nothing to do — isUnlocked() fails open when storage is unavailable.
  }
}

/**
 * How many questions this person has already asked on the active profile.
 * Counts user messages; the assistant's greeting is not one.
 */
export function questionsAsked(messages) {
  if (!Array.isArray(messages)) return 0;
  return messages.filter((m) => m.role === 'user').length;
}

/**
 * Whether the next question should be blocked.
 * @param {Array} messages the active profile's history
 */
export function shouldPaywall(messages) {
  if (isUnlocked()) return false;
  return questionsAsked(messages) >= FREE_QUESTIONS;
}

/**
 * Hosted checkout URL.
 *
 * Dodo returns the person with ?status= and ?subscription_id=. The app hands
 * that id to the server, which confirms it with Dodo and signs them in, so
 * paying and having an account are one step rather than two.
 */
export function checkoutUrl() {
  const returnTo = `${window.location.origin}${window.location.pathname}`;
  const params = new URLSearchParams({ redirect_url: returnTo });
  return `${DODO_CHECKOUT_BASE}/${DODO_PRODUCT_ID}?${params.toString()}`;
}

/**
 * Read the subscription Dodo returns after checkout and strip it from the URL,
 * so a refresh or a shared link cannot replay it.
 *
 * Note this grants nothing on its own: the id is only a claim the person's
 * browser is making, and the server checks it with Dodo before it means
 * anything. An earlier version unlocked on a bare ?unlocked=1, which anyone
 * could have typed.
 *
 * @returns {string|null} the subscription id, when this load followed a purchase
 */
export function consumeCheckoutReturn() {
  try {
    const params = new URLSearchParams(window.location.search);
    const subscriptionId = params.get('subscription_id');
    if (!subscriptionId) return null;

    params.delete('subscription_id');
    params.delete('status');
    params.delete('payment_id');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '')
    );
    return subscriptionId;
  } catch (e) {
    return null;
  }
}
